import { Button, Chip } from "@heroui/react";
import HintTooltip from "@/components/common/HintTooltip";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { KeyRound, Trash2 } from "lucide-react";

import EditableTextCell from "@/components/common/EditableTextCell";
import InlineEditCell from "@/components/common/InlineEditCell";
import type { UserRole } from "@/lib/auth";
import type { AppUser, UserInput } from "./api";
import { ROLE_LABEL } from "./roles";

/**
 * ユーザー一覧の列定義。
 *
 * パスワードは一覧からは触れない。管理者は仮パスワードの再発行（操作列）、
 * 本人はパスワード変更画面、と経路を分けている（管理者が他人のパスワードを
 * 直接決められると、その値を知った状態になるため）。
 */
export interface UserColumnHandlers {
  canWrite: boolean;
  /** ログイン中の自分。自分自身の無効化・権限解除は API が拒否するため、先に止める。 */
  currentUserId: string | undefined;
  onEditField: (user: AppUser, input: UserInput) => Promise<string | null>;
  onEditRole: (user: AppUser) => void;
  onToggleDisabled: (user: AppUser) => void;
  onIssueTemporaryPassword: (user: AppUser) => void;
  onDelete: (user: AppUser) => void;
}

const helper = createColumnHelper<AppUser>();

/** ISO 日時を YYYY-MM-DD で表示する。 */
function formatDate(value: string | null): string {
  return value ? value.slice(0, 10) : "—";
}

export function buildUserColumns({
  canWrite,
  currentUserId,
  onEditField,
  onEditRole,
  onToggleDisabled,
  onIssueTemporaryPassword,
  onDelete,
}: UserColumnHandlers): ColumnDef<AppUser, never>[] {
  return [
    helper.accessor("email", {
      id: "email",
      header: "メールアドレス",
      size: 260,
      meta: {
        label: "メールアドレス",
        mandatory: true,
        pinned: true,
        sortKey: "email",
      },
      cell: (ctx) => (
        <InlineEditCell
          label="メールアドレス"
          value={ctx.getValue()}
          canWrite={canWrite}
          onSave={(v) => onEditField(ctx.row.original, { email: v ?? "" })}
        />
      ),
    }),
    helper.accessor("name", {
      id: "name",
      header: "表示名",
      size: 180,
      meta: { label: "表示名", sortKey: "name" },
      cell: (ctx) => (
        <InlineEditCell
          label="表示名"
          value={ctx.getValue()}
          canWrite={canWrite}
          onSave={(v) => onEditField(ctx.row.original, { name: v })}
        />
      ),
    }),
    helper.accessor("role", {
      id: "role",
      header: "権限",
      size: 140,
      meta: { label: "権限" },
      // 権限は選択肢から選ぶのでダイアログにする。
      cell: (ctx) => (
        <EditableTextCell
          label="権限"
          value={ROLE_LABEL[ctx.getValue() as UserRole]}
          canWrite={canWrite}
          onEdit={() => onEditRole(ctx.row.original)}
        />
      ),
    }),
    helper.accessor("disabled", {
      id: "disabled",
      header: "状態",
      size: 110,
      meta: { label: "状態" },
      cell: (ctx) => {
        const user = ctx.row.original;
        const chip = (
          <Chip size="sm" variant="flat" color={ctx.getValue() ? "danger" : "success"}>
            {ctx.getValue() ? "無効" : "有効"}
          </Chip>
        );
        // 自分自身を無効化すると管理操作から締め出されるため押させない。
        if (!canWrite || user.user_id === currentUserId) return chip;
        return (
          <button
            type="button"
            className="cursor-pointer"
            onClick={() => onToggleDisabled(user)}
            aria-label={ctx.getValue() ? "有効にする" : "無効にする"}
          >
            {chip}
          </button>
        );
      },
    }),
    helper.accessor("must_change_password", {
      id: "must_change_password",
      header: "パスワード",
      size: 200,
      meta: { label: "パスワードの状態" },
      cell: (ctx) => {
        const user = ctx.row.original;
        if (!ctx.getValue()) {
          return <span className="text-default-500">設定済み</span>;
        }
        const expires = user.temp_password_expires_at;
        const expired = expires != null && expires < new Date().toISOString();
        return (
          <span className={expired ? "text-danger" : "text-default-700"}>
            仮パスワード
            {expires && (
              <span className="ml-1 text-tiny">
                （{expired ? "期限切れ " : "〜"}
                {expires.slice(0, 10)}）
              </span>
            )}
          </span>
        );
      },
    }),
    helper.accessor("created_at", {
      id: "created_at",
      header: "作成日",
      size: 120,
      meta: { label: "作成日", sortKey: "created_at" },
      cell: (ctx) => formatDate(ctx.getValue()),
    }),
    helper.accessor("updated_at", {
      id: "updated_at",
      header: "更新日",
      size: 120,
      meta: { label: "更新日" },
      cell: (ctx) => formatDate(ctx.getValue()),
    }),
    helper.display({
      id: "actions",
      header: "操作",
      size: 110,
      enableResizing: false,
      meta: { label: "操作", mandatory: true },
      cell: (ctx) => {
        const user = ctx.row.original;
        const isSelf = user.user_id === currentUserId;
        return (
          <div className="flex items-center gap-0.5">
            <HintTooltip
              content={canWrite ? "仮パスワードを再発行" : "管理者の権限が必要です"}
            >
              {/* 無効なボタンはイベントを出さないため、Tooltip 用に包む。 */}
              <span className="inline-flex">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  aria-label={`${user.email} の仮パスワードを再発行`}
                  isDisabled={!canWrite}
                  onPress={() => onIssueTemporaryPassword(user)}
                >
                  <KeyRound size={16} />
                </Button>
              </span>
            </HintTooltip>
            <HintTooltip
              content={
                isSelf
                  ? "自分自身は削除できません"
                  : canWrite
                    ? "削除"
                    : "管理者の権限が必要です"
              }
            >
              <span className="inline-flex">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="danger"
                  aria-label={`${user.email} を削除`}
                  isDisabled={!canWrite || isSelf}
                  onPress={() => onDelete(user)}
                >
                  <Trash2 size={16} />
                </Button>
              </span>
            </HintTooltip>
          </div>
        );
      },
    }),
  ] as ColumnDef<AppUser, never>[];
}

/** 列 id の一覧（設定の永続化で「未知のキー」を捨てるのに使う）。 */
export const userColumnIds = [
  "email",
  "name",
  "role",
  "disabled",
  "must_change_password",
  "created_at",
  "updated_at",
  "actions",
];

/** 左に固定する列の id。 */
export const userPinnedColumnIds = ["email"];
